/**
 * Pure timeline resize math (no DOM, no API).
 * Extracted from TimelineEditor resize logic: overlap with neighbors + audio speed bounds.
 */

export type TimelineSegmentInterval = {
    start: number;
    end: number;
};

export type IndexedSegment = {
    element: HTMLElement;
    start: number;
    end: number;
};

const MIN_DURATION = 0.001;
const SPEED_MIN = 0.5;
const SPEED_MAX = 2.0;
const SPEED_EPS = 1e-6;

/** 由原始时长与倍速反推：当前时长落在何区间可使语速在 [SPEED_MIN, SPEED_MAX] */
function durationBoundsForSpeed(originalDuration: number, originalSpeed: number): { min: number; max: number } {
    const minDur = (originalDuration * originalSpeed) / SPEED_MAX;
    const maxDur = (originalDuration * originalSpeed) / SPEED_MIN;
    return { min: minDur, max: maxDur };
}

export function sortSegmentsByStart(segments: IndexedSegment[]): IndexedSegment[] {
    return [...segments].sort((a, b) => a.start - b.start);
}

/** Map pointer X inside track to timeline seconds. */
export function clientXToTime(
    clientX: number,
    trackContentRect: DOMRect,
    totalTime: number
): number {
    const clickX = clientX - trackContentRect.left;
    const percentage = Math.max(0, Math.min(1, clickX / trackContentRect.width));
    return percentage * totalTime;
}

/** 左邻句 end 已压入本句时间窗（重叠）时，不再用「必须 ≥ 左邻 end」把可拖区间锁死 */
function leftNeighborFloorForResize(sorted: IndexedSegment[], index: number): number {
    if (index <= 0) return Number.NEGATIVE_INFINITY;
    const left = sorted[index - 1];
    const cur = sorted[index];
    return left.end > cur.start + SPEED_EPS ? cur.start : left.end;
}

/** 右邻句 start 落在当前句结束之前（重叠）时，暂不按右邻 start 封顶，否则右缘无法拉回 */
function rightNeighborCeilForResize(sorted: IndexedSegment[], index: number): number {
    if (index >= sorted.length - 1) return Number.POSITIVE_INFINITY;
    const cur = sorted[index];
    const right = sorted[index + 1];
    return right.start < cur.end - SPEED_EPS ? Number.POSITIVE_INFINITY : right.start;
}

export function computeResizeLeftResult(params: {
    newTimeFromPointer: number;
    endTime: number;
    currentIndex: number;
    sortedSegments: IndexedSegment[];
    originalStart: number;
    originalEnd: number;
    originalSpeed: number;
}): { start: number; end: number } | null {
    const { newTimeFromPointer, endTime, currentIndex, sortedSegments, originalStart, originalEnd, originalSpeed } =
        params;

    const neighborFloor = leftNeighborFloorForResize(sortedSegments, currentIndex);
    const originalDuration = originalEnd - originalStart;
    if (originalDuration <= 0) {
        return null;
    }

    const { min: durMinSpeed, max: durMaxSpeed } = durationBoundsForSpeed(originalDuration, originalSpeed);
    const durLo = Math.max(MIN_DURATION, durMinSpeed);
    const durHi = Math.min(endTime - neighborFloor, durMaxSpeed);
    if (durHi < durLo - SPEED_EPS) {
        return null;
    }

    const rawStart = Math.max(
        neighborFloor,
        Math.min(newTimeFromPointer, endTime - MIN_DURATION)
    );
    let dur = endTime - rawStart;
    dur = Math.max(durLo, Math.min(dur, durHi));
    const newStart = endTime - dur;

    return { start: newStart, end: endTime };
}

export function computeResizeRightResult(params: {
    newTimeFromPointer: number;
    startTime: number;
    currentIndex: number;
    sortedSegments: IndexedSegment[];
    originalStart: number;
    originalEnd: number;
    originalSpeed: number;
}): { start: number; end: number } | null {
    const { newTimeFromPointer, startTime, currentIndex, sortedSegments, originalStart, originalEnd, originalSpeed } =
        params;

    const neighborCeil = rightNeighborCeilForResize(sortedSegments, currentIndex);
    const originalDuration = originalEnd - originalStart;
    if (originalDuration <= 0) {
        return null;
    }

    const { min: durMinSpeed, max: durMaxSpeed } = durationBoundsForSpeed(originalDuration, originalSpeed);
    const durLo = Math.max(MIN_DURATION, durMinSpeed);
    const spanToNeighbor = Number.isFinite(neighborCeil) ? neighborCeil - startTime : Number.POSITIVE_INFINITY;
    const durHi = Math.min(spanToNeighbor, durMaxSpeed);
    if (durHi < durLo - SPEED_EPS) {
        return null;
    }

    let rawEnd = Math.max(newTimeFromPointer, startTime + MIN_DURATION);
    if (Number.isFinite(neighborCeil)) {
        rawEnd = Math.min(rawEnd, neighborCeil);
    }
    let dur = rawEnd - startTime;
    dur = Math.max(durLo, Math.min(dur, durHi));
    const newEnd = startTime + dur;

    return { start: startTime, end: newEnd };
}

const ORIGIN_MIN_DURATION = 0.04;

/**
 * 纯净校对：仅平移/拉长原文在轴上的时间范围，不触发语速约束（不调用倍速相关接口）
 */
export function computeOriginTimestampResizeLeft(params: {
    newTimeFromPointer: number;
    endTime: number;
    currentIndex: number;
    sortedSegments: IndexedSegment[];
}): { start: number; end: number } | null {
    const { newTimeFromPointer, endTime, currentIndex, sortedSegments } = params;
    let newStart = Math.min(newTimeFromPointer, endTime - ORIGIN_MIN_DURATION);
    if (currentIndex > 0) {
        newStart = Math.max(newStart, sortedSegments[currentIndex - 1].end);
    }
    newStart = Math.max(0, newStart);
    if (endTime - newStart < ORIGIN_MIN_DURATION) {
        return null;
    }
    return { start: newStart, end: endTime };
}

export function computeOriginTimestampResizeRight(params: {
    newTimeFromPointer: number;
    startTime: number;
    currentIndex: number;
    sortedSegments: IndexedSegment[];
}): { start: number; end: number } | null {
    const { newTimeFromPointer, startTime, currentIndex, sortedSegments } = params;
    let newEnd = Math.max(newTimeFromPointer, startTime + ORIGIN_MIN_DURATION);
    if (currentIndex < sortedSegments.length - 1) {
        newEnd = Math.min(newEnd, sortedSegments[currentIndex + 1].start);
    }
    if (newEnd - startTime < ORIGIN_MIN_DURATION) {
        return null;
    }
    return { start: startTime, end: newEnd };
}
